import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  Loader2,
  ArrowRight,
  ArrowUpRight,
  RotateCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ----- Tool configs -----
const TOOLS = {
  names: {
    slug: "names",
    path: "/brand-name-generator",
    overline: "Free · AI · 3 goes/day",
    h1: ["Brand name", "in 60 seconds."],
    h1Accent: "in 60 seconds.",
    sub: "Drop one line about your idea. Get 10 distinctive brand-name directions — each with a short rationale. No login, no email, no credit card.",
    placeholder:
      "A slow-fashion clothing label using recycled materials, for design-led urban professionals.",
    button: "Generate 10 names",
    resultKicker: "10 names · ranked by distinctiveness",
    metaTitle:
      "Free Brand Name Generator · AI-powered, no signup | The Story Assembly",
    metaDescription:
      "Generate 10 distinctive brand name ideas in seconds. AI-powered, free, no signup required. Includes rationale for each name. From The Story Assembly.",
  },
  taglines: {
    slug: "taglines",
    path: "/tagline-roulette",
    overline: "Free · AI · 3 goes/day",
    h1: ["10 taglines.", "One spin."],
    h1Accent: "One spin.",
    sub: "Pop in a line about your brand. Get 10 tagline directions across different registers — punchy, soulful, declarative, witty. Pick the one that sounds like a person, not a brand.",
    placeholder:
      "Loomtide makes handwoven home textiles for design-led homeowners who want craft that lasts.",
    button: "Spin 10 taglines",
    resultKicker: "10 taglines · across registers",
    metaTitle:
      "Free Tagline Generator · 10 ideas in 60 seconds | The Story Assembly",
    metaDescription:
      "AI tagline generator that returns 10 directions across different registers — punchy, soulful, witty. Free, no signup. From The Story Assembly.",
  },
};

const MiniToolPage = ({ tool }) => {
  const cfg = TOOLS[tool];
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const prev = document.title;
    document.title = cfg.metaTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) metaDesc.setAttribute("content", cfg.metaDescription);
    return () => {
      document.title = prev;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [cfg.metaTitle, cfg.metaDescription]);

  const submit = async (e) => {
    e.preventDefault();
    if (idea.trim().length < 4) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/mini-tools/${tool}`, {
        idea: idea.trim(),
      });
      setItems(data.items || []);
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't generate";
      if (err.response?.status === 429) {
        setError("You've hit today's free limit (3/day). Try the full demo or come back tomorrow.");
      } else {
        setError(typeof msg === "string" ? msg : "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`mini-tool-${tool}`} className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <header className="border-b border-[#DCD7C9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
              alt="The Story Assembly"
              className="h-9 w-9 rounded-md object-cover ring-1 ring-[#1C1A17]/10"
            />
            <span className="font-serif text-xl tracking-tight">
              The <span className="italic font-light">Story</span>{" "}
              <span className="text-[#D4A53A]">Assembly</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="mini-back-home"
            className="font-mono-display text-[11px] uppercase tracking-[0.2em] text-[#6B655B] hover:text-[#0A1E3F]"
          >
            ← Full generator
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
          {cfg.overline}
        </div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 leading-[1.02] text-[#0A1E3F]">
          {cfg.h1[0]} <span className="italic font-light">{cfg.h1[1]}</span>
        </h1>
        <p className="mt-6 text-[17px] leading-relaxed text-[#1C1A17]/75 max-w-2xl">
          {cfg.sub}
        </p>

        <form onSubmit={submit} className="mt-10" data-testid={`mini-form-${tool}`}>
          <label className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
            Describe your brand in one line
          </label>
          <textarea
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            required
            rows={3}
            maxLength={500}
            placeholder={cfg.placeholder}
            data-testid={`mini-input-${tool}`}
            className="mt-3 w-full rounded-md border border-[#DCD7C9] bg-white px-4 py-3 text-[15px] focus:border-[#0A1E3F] focus:outline-none resize-none"
          />
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading || idea.trim().length < 4}
              data-testid={`mini-submit-${tool}`}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#0A1E3F] text-[#F4F1EA] text-sm font-medium hover:bg-[#0A1E3F]/90 disabled:opacity-60 transition-all active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles size={15} /> {cfg.button}
                </>
              )}
            </button>
            {items.length > 0 && !loading && (
              <button
                type="submit"
                data-testid={`mini-respin-${tool}`}
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 border border-[#DCD7C9] text-sm hover:border-[#0A1E3F] transition-all"
              >
                <RotateCcw size={14} /> Re-spin
              </button>
            )}
          </div>
          {error && (
            <p data-testid={`mini-error-${tool}`} className="mt-4 text-[13px] text-red-700">
              {error}
            </p>
          )}
        </form>

        {items.length > 0 && (
          <section className="mt-14" data-testid={`mini-results-${tool}`}>
            <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B]">
              {cfg.resultKicker}
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {items.map((it, i) => (
                <div
                  key={`${i}-${it.name}`}
                  data-testid={`mini-result-${tool}-${i}`}
                  className="border border-[#DCD7C9] rounded-md p-5 bg-white hover:border-[#D4A53A] transition-colors"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-serif text-[22px] tracking-tight text-[#0A1E3F] leading-snug">
                      {it.name}
                    </span>
                  </div>
                  <p className="mt-2 ml-7 text-[13px] text-[#1C1A17]/65 leading-relaxed">
                    {it.rationale}
                  </p>
                </div>
              ))}
            </div>

            {/* Conversion CTA */}
            <div className="mt-12 bg-[#0A1E3F] text-[#F4F1EA] rounded-md p-7 md:p-9">
              <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
                Want the full picture?
              </div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tighter mt-3 leading-snug">
                Get a brand story, voice and 4-page kit — not just{" "}
                <span className="italic">{tool === "names" ? "names" : "lines"}.</span>
              </h2>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/#demo"
                  data-testid={`mini-cta-demo-${tool}`}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium"
                >
                  Try the full generator <ArrowRight size={14} />
                </Link>
                <Link
                  to="/blog"
                  data-testid={`mini-cta-blog-${tool}`}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 border border-[#F4F1EA]/25 text-[#F4F1EA] text-sm"
                >
                  Read the Journal <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export const BrandNameGenerator = () => <MiniToolPage tool="names" />;
export const TaglineRoulette = () => <MiniToolPage tool="taglines" />;
export default MiniToolPage;
